"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Play, Loader2, RotateCcw, ArrowRight, Plus, AlertCircle } from "lucide-react"
import { useRouter } from "next/navigation"
import { useUser } from "@stackframe/stack"
import { createChapterPracticeAction } from "@/lib/actions/chapter-practice-actions"
import { ChapterPracticeStatus, type ChapterPracticeState } from "@/types/question"

interface ChapterPracticeButtonProps {
  subjectId: string
  chapterId: string
  practiceState: ChapterPracticeState
}

export function ChapterPracticeButton({
  subjectId,
  chapterId,
  practiceState,
}: ChapterPracticeButtonProps) {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const user = useUser()

  const handleAction = async (action: "continue" | "restart" | "new") => {
    // 检查用户是否已登录
    if (!user) {
      alert("请先登录后再开始练习")
      router.push("/handler/login")
      return
    }

    setLoading(true)
    try {
      if (action === "continue" && practiceState.activeSessionId) {
        // 继续未完成的练习
        router.push(`/questions/chapter-practice/${practiceState.activeSessionId}`)
      } else {
        // 确定练习类型
        let practiceType: "FIRST_TIME" | "RETRY" | "NEW_QUESTIONS" = "FIRST_TIME"

        if (action === "restart") {
          practiceType = "RETRY"
        } else if (action === "new") {
          practiceType = "NEW_QUESTIONS"
        }

        // 创建新的章节练习
        const result = await createChapterPracticeAction(chapterId, practiceType)

        if (result.success && result.data) {
          router.push(`/questions/chapter-practice/${result.data.id}`)
        } else {
          console.error("创建章节练习失败:", result.error)
          alert(result.error || "创建章节练习失败，请稍后重试")
        }
      }
    } catch (error) {
      console.error("章节练习操作失败:", error)
      alert("操作失败，请稍后重试")
    } finally {
      setLoading(false)
    }
  }

  const getButtonConfig = () => {
    switch (practiceState.status) {
      case ChapterPracticeStatus.NEVER_STARTED:
        return {
          primary: {
            icon: Play,
            text: "开始练习",
            action: () => handleAction("restart"),
            variant: "default" as const,
          },
        }

      case ChapterPracticeStatus.IN_PROGRESS:
        return {
          primary: {
            icon: ArrowRight,
            text: "继续练习",
            action: () => handleAction("continue"),
            variant: "default" as const,
          },
          secondary: {
            icon: RotateCcw,
            text: "重新开始",
            action: () => handleAction("restart"),
            variant: "outline" as const,
          },
        }

      case ChapterPracticeStatus.COMPLETED_OUTDATED:
        return {
          primary: {
            icon: Plus,
            text: `练习新题目 (${practiceState.newQuestionsCount}题)`,
            action: () => handleAction("new"),
            variant: "default" as const,
          },
          secondary: {
            icon: RotateCcw,
            text: "重新练习",
            action: () => handleAction("restart"),
            variant: "outline" as const,
          },
        }

      case ChapterPracticeStatus.COMPLETED_CURRENT:
        return {
          primary: {
            icon: RotateCcw,
            text: "重新练习",
            action: () => handleAction("restart"),
            variant: "outline" as const,
          },
        }

      default:
        return {
          primary: {
            icon: Play,
            text: "开始练习",
            action: () => handleAction("restart"),
            variant: "default" as const,
          },
        }
    }
  }

  const buttonConfig = getButtonConfig()

  return (
    <div className="space-y-2">
      {/* 状态提示 */}
      {practiceState.status === ChapterPracticeStatus.COMPLETED_OUTDATED && (
        <div className="flex items-center text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded">
          <AlertCircle className="h-3 w-3 mr-1" />
          发现 {practiceState.newQuestionsCount} 道新题目
        </div>
      )}

      {/* 按钮组 */}
      <div className="flex items-center gap-2">
        {/* 主要按钮 */}
        <Button
          onClick={buttonConfig.primary.action}
          disabled={loading}
          variant={buttonConfig.primary.variant}
          className={`flex-grow transition-all duration-200 ${loading ? "opacity-80" : ""}`}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <buttonConfig.primary.icon className="h-4 w-4 mr-2" />
          )}
          {loading ? "创建中..." : buttonConfig.primary.text}
        </Button>

        {/* 次要按钮或占位符 */}
        <div className="w-10 h-10 flex-shrink-0">
          {buttonConfig.secondary && (
            <Button
              onClick={buttonConfig.secondary.action}
              disabled={loading}
              variant={buttonConfig.secondary.variant}
              size="icon"
              className="h-full w-full"
            >
              <buttonConfig.secondary.icon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
